import { supabase } from '../../shared/lib/supabase'
import { personalizedPresets } from '../onboarding/string'
import type { UserData } from './user-types'

/**
 * Fetches the current user's profile and their AI agent from Supabase.
 * Returns null if not authenticated or on any error (caller should fall back to mock).
 */
export async function getUserProfile(): Promise<UserData | null> {
  try {
    const { data: { user: authUser } } = await supabase.auth.getUser()
    if (!authUser) return null

    const [usersRes, agentRes] = await Promise.all([
      supabase.from('users').select('*').single(),
      supabase.from('ai_agents').select('display_name, email').single(),
    ])

    if (usersRes.error || !usersRes.data) return null
    if (agentRes.error || !agentRes.data) return null

    const u = usersRes.data
    const agent = agentRes.data

    const name = [u.first_name, u.last_name].filter(Boolean).join(' ') || 'User'
    const lang = (u.preferences as Record<string, unknown>)?.language as string | undefined
    const isSv = lang === 'sv'
    const presetFallback = personalizedPresets((u.first_name as string) ?? '', isSv ? 'sv' : 'en')[0]?.name
    const genericFallback = isSv ? 'Assistent' : 'Assistant'
    const agentName = agent.display_name || presetFallback || genericFallback

    return {
      id: u.id,
      name,
      email: u.email,
      phone: undefined,
      timezone: (u.preferences as Record<string, unknown>)?.timezone as string || 'Europe/Stockholm',
      subscription: (() => {
        const tier = u.tier || 'trial'
        const trialEndsAt = u.trial_ends_at ?? null
        const trialDaysLeft = trialEndsAt
          ? Math.max(0, Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
          : null
        const isAnnual = u.billing_interval === 'year'
        return {
          tier,
          price: tier === 'managed' ? (isAnnual ? 190 : 19) : 0,
          currency: 'USD',
          billingCycle: isAnnual ? 'annual' : 'monthly',
          status: u.subscription_status || (tier === 'trial' ? 'trial' : 'active'),
          nextBilling: '',
          features: u.addons || [],
          trialEndsAt,
          trialDaysLeft,
          subscriptionStatus: u.subscription_status ?? null,
        }
      })(),
      assistant: {
        name: agentName,
        email: agent.email,
        avatar: agentName.charAt(0).toUpperCase(),
        stats: { tasksCompleted: 0, daysActive: 0, approvalRate: 0 },
      },
      preferences: {
        theme: 'dark',
        timezone: (u.preferences as Record<string, unknown>)?.timezone as string || 'Europe/Stockholm',
        dailyResetHour: (u.preferences as Record<string, unknown>)?.dailyResetHour as number ?? 4,
        idleTimeoutMinutes: (u.preferences as Record<string, unknown>)?.idleTimeoutMinutes as number ?? 240,
        notifications: { push: true, email: true, sms: false },
        briefing: {
          enabled: false,
          deliveryTime: '07:00',
          weekdaysOnly: true,
          channels: ['chat'] as ('chat' | 'email')[],
          sources: { calendar: true, emails: true, approvals: true, followups: true, notebook: true },
          ...((u.preferences as Record<string, unknown>)?.briefing as Record<string, unknown> || {}),
        },
      },
      connectedServices: [],
      usage: {
        requests: { used: 0, limit: 'Unlimited' },
        storage: { used: '0 GB', limit: '10 GB' },
      },
    }
  } catch {
    return null
  }
}

